import { useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import { CheckCircle2, Pencil, Trash2 } from 'lucide-react';
import { ResolveDealCostDialog } from '@/components/deal-costs/ResolveDealCostDialog';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { buildMutationErrorHandler } from '@/utils/form-errors';
import {
  useDealCosts,
  useDealCostsSummary,
  useUpdateDealCost,
  useDeleteDealCost,
  useResolveDealCost,
} from '@/hooks/react-query/useDealCosts';
import {
  DealCostType,
  DEAL_COST_TYPE_LABELS,
  type DealCost,
  type DealCostOutcome,
  type DealCostResolve,
} from '@/types/deal-costs';

const OUTCOME_BADGE: Partial<Record<DealCostOutcome, string>> = {
  PENDING: 'Pending',
  ABORTED: 'Broken deal',
};

const EMPTY_FORM = {
  cost_type: '' as DealCostType | '',
  description: '',
};

interface DealCostsSectionProps {
  fundId: string;
  fundCurrency: string;
}

interface DealCostsTableProps {
  isLoading: boolean;
  costs: DealCost[];
  fundCurrency: string;
  onResolve: (cost: DealCost) => void;
  onEdit: (cost: DealCost) => void;
  onDelete: (cost: DealCost) => void;
}

function DealCostsTable({
  isLoading,
  costs,
  fundCurrency,
  onResolve,
  onEdit,
  onDelete,
}: DealCostsTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading deal costs...
      </div>
    );
  }
  if (costs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No deal cost yet. Book one from an outgoing bank line in Incoming
        Transactions.
      </div>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Deal</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead className="w-[80px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {costs.map((cost) => (
          <TableRow key={cost.id}>
            <TableCell className="font-medium">
              <div className="flex items-center gap-2">
                {cost.portfolio_company_name ?? cost.deal_name}
                {OUTCOME_BADGE[cost.outcome] && (
                  <Badge variant="secondary">
                    {OUTCOME_BADGE[cost.outcome]}
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell>
              <Badge variant="outline">
                {DEAL_COST_TYPE_LABELS[cost.cost_type]}
              </Badge>
            </TableCell>
            <TableCell>{cost.description || '-'}</TableCell>
            <TableCell>{formatDate(cost.cost_date)}</TableCell>
            <TableCell className="text-right">
              {formatCurrency(cost.amount, { currency: fundCurrency })}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-1">
                {cost.outcome === 'PENDING' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1"
                    onClick={() => onResolve(cost)}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Resolve
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onEdit(cost)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={() => onDelete(cost)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function FundFeesDealCosts({
  fundId,
  fundCurrency,
}: DealCostsSectionProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { data: costsPage, isLoading } = useDealCosts(fundId, {
    page,
    page_size: pageSize,
  });
  const { data: costsSummary } = useDealCostsSummary(fundId);
  const updateMutation = useUpdateDealCost(fundId);
  const deleteMutation = useDeleteDealCost(fundId);
  const resolveMutation = useResolveDealCost(fundId);

  const [editingCost, setEditingCost] = useState<DealCost | null>(null);
  const [resolvingCost, setResolvingCost] = useState<DealCost | null>(null);
  const [deletingCost, setDeletingCost] = useState<DealCost | null>(null);
  const [costForm, setCostForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const totalCosts = costsSummary?.total_costs ?? 0;
  const costs = costsPage?.items ?? [];
  const totalCount = costsPage?.total_count ?? 0;

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const openEditDialog = (cost: DealCost) => {
    setEditingCost(cost);
    setCostForm({
      cost_type: cost.cost_type,
      description: cost.description ?? '',
    });
    setFormErrors({});
  };

  const closeDialog = () => {
    setEditingCost(null);
    setCostForm(EMPTY_FORM);
  };

  const handleSubmitCost = () => {
    const { cost_type: costType } = costForm;
    if (!editingCost || !costType) {
      setFormErrors({ cost_type: 'Cost type is required' });
      return;
    }

    updateMutation.mutate(
      {
        costId: editingCost.id,
        data: {
          cost_type: costType,
          description: costForm.description || null,
        },
      },
      {
        onSuccess: closeDialog,
        onError: buildMutationErrorHandler(setFormErrors, {
          // This dialog has no general-error slot; non-field errors rely on
          // the mutation's own toast instead.
          onFallbackError: () => {},
        }),
      }
    );
  };

  const handleResolve = async (costId: string, data: DealCostResolve) => {
    await resolveMutation.mutateAsync({ costId, data });
  };

  const handleDeleteConfirm = () => {
    if (!deletingCost) return;
    deleteMutation.mutate(deletingCost.id, {
      onSuccess: () => setDeletingCost(null),
    });
  };

  const isMutating = updateMutation.isPending || deleteMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Deal Costs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalCosts, { currency: fundCurrency })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Deal Cost</CardTitle>
        </CardHeader>
        <CardContent>
          <DealCostsTable
            isLoading={isLoading}
            costs={costs}
            fundCurrency={fundCurrency}
            onResolve={setResolvingCost}
            onEdit={openEditDialog}
            onDelete={setDeletingCost}
          />
          {totalCount > 0 && (
            <Pagination
              current={page}
              total={totalCount}
              pageSize={pageSize}
              onChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Edit Cost Dialog */}
      <Dialog
        open={editingCost !== null}
        onOpenChange={(open) => !open && closeDialog()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Deal Cost</DialogTitle>
            <DialogDescription>
              Amount, date and investment come from the bank line.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deal-cost-type">Cost Type</Label>
              <Select
                value={costForm.cost_type}
                onValueChange={(value) =>
                  setCostForm({
                    ...costForm,
                    cost_type: value as DealCostType,
                  })
                }
              >
                <SelectTrigger id="deal-cost-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(DealCostType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {DEAL_COST_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.cost_type && (
                <p className="text-sm text-destructive">
                  {formErrors.cost_type}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="deal-cost-description">Description</Label>
              <Input
                id="deal-cost-description"
                placeholder="e.g., Legal review for acquisition"
                value={costForm.description}
                onChange={(e) =>
                  setCostForm({ ...costForm, description: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmitCost} disabled={isMutating}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResolveDealCostDialog
        cost={resolvingCost}
        fundId={fundId}
        isPending={resolveMutation.isPending}
        onResolve={handleResolve}
        onClose={() => setResolvingCost(null)}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingCost}
        onOpenChange={(open) => !open && setDeletingCost(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deal Cost</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this{' '}
              {deletingCost &&
                DEAL_COST_TYPE_LABELS[
                  deletingCost.cost_type
                ].toLowerCase()}{' '}
              cost for{' '}
              {deletingCost?.portfolio_company_name ?? deletingCost?.deal_name}{' '}
              (
              {formatCurrency(deletingCost?.amount ?? 0, {
                currency: fundCurrency,
              })}
              )? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
